import type {
  ChangeEventHandler,
  InputHTMLAttributes,
  MouseEventHandler,
  ReactNode,
} from 'react';

import { BasePopover } from '../BasePopover';
import { BaseTooltip } from '../BaseTooltip';
import {
  BaseButtonInput,
  CloseIcon,
  CustomSearchIcon,
  InputSearch,
  PopoverContent,
  SearchCloseButton,
  SearchFieldset,
  SearchIcon,
} from './styles';

export interface ISearchBarProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'defaultValue' | 'value'
  > {
  search: string;
  placeholder?: string;
  loading?: boolean;
  dataCy?: string;
  customSearch?: boolean;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onClearInput?: MouseEventHandler<HTMLButtonElement> | null;
  id?: string;
  isOpenPopover?: boolean;
  handleOpenPopover?: () => void;
  childrenPopover?: ReactNode;
  hasActiveFilters?: boolean;
}

export const SearchBar = ({
  search,
  placeholder = 'Search',
  dataCy = placeholder,
  customSearch = false,
  loading = false,
  onChange,
  onClearInput = null,
  id = 'input-search',
  isOpenPopover = false,
  childrenPopover,
  handleOpenPopover,
  hasActiveFilters = false,
  ...props
}: ISearchBarProps): JSX.Element => (
  <SearchFieldset>
    <SearchIcon />

    <InputSearch
      {...props}
      placeholder={placeholder}
      value={search}
      type="text"
      onChange={onChange}
      data-cy={dataCy}
      readOnly={loading}
      id={id}
    />

    {onClearInput && (
      <BaseTooltip message="Clear search" data-cy="tooltip-clear-search">
        <SearchCloseButton
          type="button"
          onClick={(event) => {
            if (!loading) onClearInput(event);
          }}
          $visible={search !== ''}
          data-cy="button-cancelSearch"
        >
          <CloseIcon />
        </SearchCloseButton>
      </BaseTooltip>
    )}

    {customSearch && (
      <BasePopover
        trigger={
          <BaseButtonInput type="button" data-cy="button-custom-search">
            <CustomSearchIcon $hasActiveFilters={hasActiveFilters} />
          </BaseButtonInput>
        }
        open={isOpenPopover}
        onOpenChange={handleOpenPopover}
        zIndex={10}
        popoverMarginRight={10}
      >
        <PopoverContent>{childrenPopover}</PopoverContent>
      </BasePopover>
    )}
  </SearchFieldset>
);
